import { useState, FormEvent, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import IMask from "imask";
import { IMaskInput } from "react-imask";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const CURRENCIES = [
  "USD",
  "EUR",
  "GBP",
  "CAD",
  "AUD",
  "CHF",
  "JPY",
  "CNY",
  "INR",
  "Other"
];

export default function DisputeResolutionPage() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Record<string, string>>({});
  const [errorSubmit, setErrorSubmit] = useState(false);
  const [description, setDescription] = useState("");
  const [formData, setFormData] = useState<Record<string, string>>({
    name: "",
    email: "",
    date_of_transaction: "",
    amount: "",
    currency: "",
    card_first6: "",
    card_last4: "",
    description: ""
  });

  const descLen = description.length;
  const descValid = descLen >= 20 && descLen <= 500;

  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (submitted) {
      setTimeout(() => {
        topRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 50);
    }
  }, [submitted]);

  const currentDay = new Date()

  function updateField(name: string, value: string) {
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(prev => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }

  function validation(): boolean {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    if (
      !formData.email.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    ) {
      newErrors.email = "Valid email is required";
    }

    if (!formData.date_of_transaction.trim()) {
      newErrors.date_of_transaction = "Date is required";
    } else if (formData.date_of_transaction.length < 10) {
      newErrors.date_of_transaction = "Please enter a complete date";
    } else {
      const [dd, mm, yyyy] = formData.date_of_transaction.split('/').map(Number);
      const entered = new Date(yyyy, mm - 1, dd);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      today.setDate(today.getDate() + 1);
      if (entered > today) {
        newErrors.date_of_transaction = "Date cannot be in the future";
      }
    }

    if (!formData.amount.trim() || isNaN(Number(formData.amount))) {
      newErrors.amount = "Valid amount is required";
    } else if (Number(formData.amount) <= 0) {
      newErrors.amount = "Amount must be greater than 0";
    }

    if (!formData.currency) {
      newErrors.currency = "Please select a currency";
    }

    if (!formData.card_first6.trim() || formData.card_first6.length < 6) {
      newErrors.card_first6 = "Must be 6 digits";
    }

    if (!formData.card_last4.trim() || formData.card_last4.length < 4) {
      newErrors.card_last4 = "Must be 4 digits";
    }

    if (formData.description.trim().length < 20) {
      newErrors.description = "Minimum 20 characters required";
    } else if (formData.description.trim().length > 500) {
      newErrors.description = "Maximum 500 characters allowed";
    }

    setError(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validation()) return;

    setLoading(true);

    let text = "";
    for (const [key, value] of Object.entries(formData)) {
      text += `${key} - ${value}\n`;
    }

    const payload = {
      email: formData.email,
      text,
      problemType: "Transaction Issue",
      to_email: "support@regent-digital.com",
      subject: "Support Request"
    };

    try {
      const res = await fetch(
        "https://rocknlabs.com/api/support/request_form",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        }
      );

      if (!res.ok) throw new Error("Server error");

      setSubmitted(true);
    } catch {
      setErrorSubmit(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div ref={topRef} className="min-h-screen flex flex-col bg-white font-sans">
      <style>{`
      input:-webkit-autofill,
      input:-webkit-autofill:hover,
      input:-webkit-autofill:focus {
        -webkit-box-shadow: 0 0 0rem 62.5rem white inset;
      }
    `}</style>
      <Navbar />

      {/* Page header */}
      <div className="bg-[#F5C518] pt-28 pb-12 px-6 sm:px-10">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs font-black tracking-widest uppercase text-black/50 hover:text-black transition-colors cursor-pointer mb-6"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </div>
            Back
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
              <i className="ri-file-text-line text-[#F5C518] text-xl"></i>
            </div>
            <h1 className="text-3xl sm:text-5xl font-black uppercase tracking-tight text-black leading-none">
              Dispute Resolution
            </h1>
          </div>
          <div className="w-16 h-1 bg-black mt-4"></div>
        </div>
      </div>

      <main className="flex-1 max-w-6xl mx-auto w-full px-6 sm:px-10 py-16">
        <div className="max-w-2xl">
          <div className="mb-10">
            <p className="text-black/70 text-base leading-relaxed">
              Submit formal dispute resolution requests through our structured
              process for comprehensive issue resolution. All disputes are
              reviewed within 5–7 business days.
            </p>
          </div>

          {submitted ? (
            <div className="bg-[#F5C518] p-8 sm:p-10 text-center">
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-[#F5C518] text-xl"></i>
              </div>
              <h3 className="text-xl font-black uppercase tracking-tight text-black mb-2">
                Dispute Submitted
              </h3>
              <p className="text-black/60 text-sm">
                Your dispute has been received. We will review it and respond
                within 14 business days.
              </p>
              <Link
                to="/"
                className="inline-block mt-6 px-6 py-3 bg-black text-[#F5C518] text-xs font-black tracking-widest uppercase hover:bg-black/80 transition-colors cursor-pointer whitespace-nowrap"
              >
                Back to Help Center
              </Link>
            </div>
          ) : (
            <form
              id="dispute-resolution-form"
              onSubmit={handleSubmit}
              className="space-y-8"
            >
              {/* Row 1: Name + Email */}
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-black text-black mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    className="w-full border-2 border-black/20 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors bg-white rounded-lg"
                    placeholder="Enter your full name"
                    onChange={e => updateField("name", e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-black text-black mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    name="email"
                    required
                    className="w-full border-2 border-black/20 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors bg-white rounded-lg"
                    placeholder="your.email@example.com"
                    onChange={e => updateField("email", e.target.value)}
                  />
                </div>
              </div>

              {/* Row 2: Date of Transaction + Amount */}
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-black text-black mb-2">
                    Date of Transaction
                  </label>
                  <IMaskInput
                    mask="DD/MM/YYYY"
                    blocks={{
                      DD: { mask: IMask.MaskedRange, from: 1, to: 31 },
                      MM: { mask: IMask.MaskedRange, from: 1, to: 12 },
                      YYYY: { mask: IMask.MaskedRange, from: 2020, to: currentDay.getFullYear() }
                    }}
                    validate={(value: string) => {
                      if (value.length < 10) return true; 
                      const [dd, mm, yyyy] = value.split('/').map(Number);
                      const entered = new Date(yyyy, mm - 1, dd);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      today.setDate(today.getDate() + 1);
                      return entered <= today; 
                    }}
                    value={formData.date_of_transaction}
                    onAccept={value =>
                      updateField("date_of_transaction", value)
                    }
                    autoComplete="off"
                    name="date_of_transaction"
                    className="w-full border-2 border-black/20 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors bg-white rounded-lg"
                    placeholder="DD/MM/YYYY"
                  />
                  {error.date_of_transaction && (
                    <p className="text-red-500 text-xs mt-1.5">
                      {error.date_of_transaction}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-black text-black mb-2">
                    Amount
                  </label>
                  <IMaskInput
                    mask={Number}
                    scale={2}
                    min={0}
                    max={100}
                    radix="."  
                    mapToRadix={[","]} 
                    normalizeZeros
                    name="amount"
                    value={formData.amount}
                    onAccept={value => updateField("amount", value)}
                    className="w-full border-2 border-black/20 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors bg-white rounded-lg"
                    placeholder="0.00"
                  />
                  {error.amount && (
                    <p className="text-red-500 text-xs mt-1.5">
                      {error.amount}
                    </p>
                  )}
                </div>
              </div>

              {/* Row 3: Currency + First 6 digits + Last 4 digits */}
              <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_1fr] gap-4 sm:gap-6">
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-black text-black mb-2">
                    Currency
                  </label>
                  <div className="relative">
                    <select
                      name="currency"
                      value={formData.currency}
                      className="w-full border-2 border-black/20 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors bg-white rounded-lg appearance-none cursor-pointer"
                      onChange={e => updateField("currency", e.target.value)}
                    >
                      <option value="">Select currency</option>
                      {CURRENCIES.map(c => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-down-s-line text-black/50 text-base"></i>
                    </div>
                  </div>
                  {error.currency && (
                    <p className="text-red-500 text-xs mt-1.5">
                      {error.currency}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-black text-black mb-2">
                    First 6 digits
                  </label>
                  <IMaskInput
                    mask="000000"
                    name="card_first6"
                    value={formData.card_first6}
                    onAccept={value => updateField("card_first6", value)}
                    placeholder="123456"
                    className="w-full border-2 border-black/20 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors bg-white rounded-lg"
                    autoComplete="off"
                  />
                  {error.card_first6 && (
                    <p className="text-red-500 text-xs mt-1.5">
                      {error.card_first6}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-black text-black mb-2">
                    Last 4 digits
                  </label>
                  <IMaskInput
                    mask="0000"
                    name="card_last4"
                    value={formData.card_last4}
                    onAccept={value => updateField("card_last4", value)}
                    placeholder="1234"
                    className="w-full border-2 border-black/20 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors bg-white rounded-lg"
                    autoComplete="off"
                  />
                  {error.card_last4 && (
                    <p className="text-red-500 text-xs mt-1.5">
                      {error.card_last4}
                    </p>
                  )}
                </div>
              </div>

              {/* Issue Description */}
              <div>
                <label className="block text-sm font-black text-black mb-2">
                  Issue Description
                </label>
                <textarea
                  name="description"
                  required
                  maxLength={500}
                  rows={7}
                  value={formData.description}
                  onChange={e => {
                    setDescription(e.target.value);
                    updateField("description", e.target.value);
                  }}
                  className="w-full border-2 border-black/20 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors resize-none bg-white rounded-lg"
                  placeholder="Please provide detailed information about your dispute. Include what happened, when it occurred, and what resolution you are seeking."
                />
                <div className="flex items-center justify-between mt-1">
                  <span
                    className={`text-xs ${descLen > 0 && descLen < 20 ? "text-red-500" : "text-black/40"}`}
                  >
                    Minimum 20 characters required
                  </span>
                  <span
                    className={`text-xs font-semibold ${descLen > 480 ? "text-red-500" : "text-black/40"}`}
                  >
                    {descLen} / 500
                  </span>
                </div>
              </div>

              {errorSubmit && (
                <p className="text-red-500 text-sm">
                  Something went wrong. Please try again.
                </p>
              )}
              <button
                type="submit"
                disabled={loading || !descValid}
                className="w-full sm:w-auto px-8 py-3 bg-black text-[#F5C518] text-xs font-black tracking-widest uppercase hover:bg-black/80 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 rounded-lg"
              >
                {loading ? "Submitting..." : "Submit Dispute"}
              </button>
            </form>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
